'use client';

import type { LoyaltyPurchasePoints, UpcomingOrderGrooveDetails } from '@libs/common-node';
import type { Nullable } from '@libs/core';
import type { Dispatch, PropsWithChildren, SetStateAction } from 'react';
import { createContext } from 'react';

import type {
    AsyncOcapi,
    OcapiProps,
    AccountAddressesSuccessResponse,
    AccountPaymentsSuccessResponse,
    AccountAppointmentsSuccessfulResponse,
    AccountPackagesSuccessfulResponse,
    Profile,
} from '../types';

export type StorageUserSession = {
    firstName?: string;
    basketCount?: number;
    loyaltyPoints?: number;
    isRegistered?: boolean;
};

export type IdentityUser =
    | {
          aud: string;
          customerprofileid: number;
          elevatedauthexpiresat: number;
          exp: number;
          gn: string;
          iss: string;
          janrainaccesstoken: string;
          loyaltyid: number;
          rol: unknown[];
          sid: string;
          sn: string;
          sourcelocation: number;
          oktatoken?: string;
          email?: string;
      }
    | undefined;

export interface UserData {
    profile: Nullable<Profile>;
    addresses: Nullable<AccountAddressesSuccessResponse[]>;
    payments: Nullable<AccountPaymentsSuccessResponse[]>;
    appointments: Nullable<AccountAppointmentsSuccessfulResponse>;
    packages: Nullable<AccountPackagesSuccessfulResponse[]>;
}

export type UserDataToUpdate =
    | Nullable<Profile>
    | Nullable<AccountAddressesSuccessResponse[]>
    | Nullable<AccountPaymentsSuccessResponse[]>
    | Nullable<AccountAppointmentsSuccessfulResponse>
    | Nullable<AccountPackagesSuccessfulResponse[]>;

export type UserSessionProviderContextProps = {
    isAuthenticated?: 'ocapi' | 'velocity';
    loyalty?: Nullable<LoyaltyPurchasePoints>;
    ocapi: AsyncOcapi;
    ogSubscriptionId?: string;
    orderGrooveData?: Nullable<UpcomingOrderGrooveDetails>;
    refreshOcapi: (_: { isExperimentFullSolution: boolean }) => Promise<OcapiProps | null>;
    storageUserSession?: StorageUserSession;
    setOcapi: Dispatch<SetStateAction<AsyncOcapi>>;
    setUser: Dispatch<SetStateAction<IdentityUser>>;
    user?: IdentityUser;
    userData?: Nullable<UserData>;
    updateProfileData: (dataToUpdate?: string) => Promise<void>;
};

type UserSessionProviderProps = PropsWithChildren<{
    values: UserSessionProviderContextProps;
}>;

export const UserSessionProviderContext = createContext<UserSessionProviderContextProps | null>(null);

export function UserSessionProvider({ children, values }: UserSessionProviderProps) {
    return <UserSessionProviderContext.Provider value={values}>{children}</UserSessionProviderContext.Provider>;
}
